<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h3>Modifier l'Utilisateur</h3>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-row">
          <div class="form-group">
            <label for="prenom">Prénom *</label>
            <input
              id="prenom"
              v-model="form.prenom"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.prenom }"
              placeholder="Prénom"
              required
            />
            <div v-if="errors.prenom" class="form-error">
              {{ errors.prenom }}
            </div>
          </div>

          <div class="form-group">
            <label for="nom">Nom *</label>
            <input
              id="nom"
              v-model="form.nom"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.nom }"
              placeholder="Nom de famille"
              required
            />
            <div v-if="errors.nom" class="form-error">
              {{ errors.nom }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="email">Adresse email *</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email }"
            placeholder="exemple@email.com"
            required
          />
          <div v-if="errors.email" class="form-error">
            {{ errors.email }}
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="role">Rôle *</label>
            <div class="select-wrapper">
              <select
                id="role"
                v-model="form.role"
                class="form-control"
                :class="{ 'is-invalid': errors.role }"
                required
              >
                <option value="">Sélectionner un rôle</option>
                <option v-for="role in roles" :key="role.value" :value="role.value">
                  {{ role.label }}
                </option>
              </select>
            </div>
            <div v-if="errors.role" class="form-error">
              {{ errors.role }}
            </div>
          </div>

          <div class="form-group">
            <label for="actif">Statut</label>
            <div class="status-toggle">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="form.actif"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="status-label" :class="form.actif ? 'status-active' : 'status-inactive'">
                {{ form.actif ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="telephone">Téléphone</label>
          <input
            id="telephone"
            v-model="form.telephone"
            type="tel"
            class="form-control"
            :class="{ 'is-invalid': errors.telephone }"
            placeholder="+33 1 23 45 67 89"
          />
          <div v-if="errors.telephone" class="form-error">
            {{ errors.telephone }}
          </div>
        </div>

        <div class="form-group">
          <label for="adresse">Adresse</label>
          <textarea
            id="adresse"
            v-model="form.adresse"
            class="form-control"
            :class="{ 'is-invalid': errors.adresse }"
            placeholder="Adresse complète"
            rows="3"
          ></textarea>
          <div v-if="errors.adresse" class="form-error">
            {{ errors.adresse }}
          </div>
        </div>

        <div v-if="submitError" class="submit-error">
          <i class="fas fa-exclamation-triangle"></i>
          {{ submitError }}
        </div>
      </form>

      <div class="modal-footer">
        <button 
          type="button" 
          @click="closeModal" 
          class="btn btn-secondary"
        >
          Annuler
        </button>
        <button 
          @click="handleSubmit"
          :disabled="loading"
          class="btn btn-primary"
        >
          <span v-if="loading" class="loading-spinner"></span>
          <i v-else class="fas fa-save"></i>
          {{ loading ? 'Sauvegarde...' : 'Sauvegarder' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'EditUserModal',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'user-updated'],
  setup(props, { emit }) {
    const store = useStore()

    const loading = ref(false)
    const submitError = ref('')

    const form = reactive({
      prenom: '',
      nom: '',
      email: '',
      role: '',
      telephone: '',
      adresse: '',
      actif: true
    })

    const errors = reactive({
      prenom: '',
      nom: '',
      email: '',
      role: '',
      telephone: '',
      adresse: ''
    })

    const roles = computed(() => store.getters['users/roles'])

    const initializeForm = () => {
      if (props.user) {
        form.prenom = props.user.prenom || ''
        form.nom = props.user.nom || ''
        form.email = props.user.email || ''
        form.role = props.user.role || ''
        form.telephone = props.user.telephone || ''
        form.adresse = props.user.adresse || ''
        form.actif = props.user.actif !== undefined ? props.user.actif : true
      }
    }

    const validateForm = () => {
      // Reset errors
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })

      let isValid = true

      // Prénom validation
      if (!form.prenom.trim()) {
        errors.prenom = 'Le prénom est requis'
        isValid = false
      } else if (form.prenom.trim().length < 2) {
        errors.prenom = 'Le prénom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Nom validation
      if (!form.nom.trim()) {
        errors.nom = 'Le nom est requis'
        isValid = false
      } else if (form.nom.trim().length < 2) {
        errors.nom = 'Le nom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Email validation
      if (!form.email.trim()) {
        errors.email = 'L\'email est requis'
        isValid = false
      } else if (!/\S+@\S+\.\S+/.test(form.email)) {
        errors.email = 'Format d\'email invalide'
        isValid = false
      }

      // Role validation
      if (!form.role) {
        errors.role = 'Le rôle est requis'
        isValid = false
      }

      // Phone validation (optional)
      if (form.telephone && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(form.telephone)) {
        errors.telephone = 'Format de téléphone invalide'
        isValid = false
      }

      return isValid
    }

    const handleSubmit = async () => {
      submitError.value = ''

      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        await store.dispatch('users/updateUser', {
          id: props.user._id,
          userData: {
            ...form,
            prenom: form.prenom.trim(),
            nom: form.nom.trim(),
            email: form.email.trim().toLowerCase()
          }
        })

        emit('user-updated')
      } catch (error) {
        submitError.value = error.message
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    onMounted(() => {
      initializeForm()
    })

    return {
      form,
      errors,
      loading,
      submitError,
      roles,
      handleSubmit,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal {
  max-width: 600px;
  width: 100%;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.status-toggle {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: var(--success);
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.status-label {
  font-weight: 500;
  font-size: 0.875rem;
}

.status-label.status-active {
  color: var(--success);
}

.status-label.status-inactive {
  color: var(--error);
}

.submit-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal {
    margin: 1rem;
    max-width: none;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
